import { useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { UploadFamilyDocumentVariables } from '@/features/family-record/hooks/useFamilyDocumentMutations';
import type { FamilyDocumentType } from '@/features/family-record/mappers/familyDocument';

const DOCUMENT_TYPES: readonly FamilyDocumentType[] = [
  'DOCUMENT',
  'IMAGE',
  'MEDICAL_REPORT',
  'OTHER',
];

interface UploadFamilyDocumentDialogProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (variables: UploadFamilyDocumentVariables) => void;
}

/**
 * Family-attachment upload dialog. Exposes the full BE surface for
 * `POST /families/{guid}/attachment`: file, type (enum), dateObserved
 * (SimpleDate), and free-text comments. Only `file` is required.
 */
export function UploadFamilyDocumentDialog({
  busy,
  onCancel,
  onSubmit,
}: UploadFamilyDocumentDialogProps): ReactElement {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState<FamilyDocumentType>('DOCUMENT');
  const [dateObserved, setDateObserved] = useState('');
  const [comments, setComments] = useState('');

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const picked = event.target.files?.item(0) ?? null;
    setFile(picked);
  };

  const canSubmit = file !== null && !busy;

  const submit = (): void => {
    if (file === null || busy) return;
    const trimmedComments = comments.trim();
    const simpleDate = buildSimpleDate(dateObserved);
    const variables: UploadFamilyDocumentVariables = {
      file,
      type,
      ...(simpleDate !== null ? { dateObserved: simpleDate } : {}),
      ...(trimmedComments.length > 0 ? { comments: trimmedComments } : {}),
    };
    onSubmit(variables);
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogContent className="max-w-md" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.family.documents.uploadDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.family.documents.uploadDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="upload-family-doc-file">
              {t('routes.family.documents.uploadDialog.file')}
            </Label>
            <input
              ref={fileInputRef}
              id="upload-family-doc-file"
              type="file"
              onChange={handleFileChange}
              disabled={busy}
              required
              className="block w-full text-xs file:mr-3 file:rounded file:border file:border-input file:bg-background file:px-2 file:py-1 file:text-xs"
            />
            {file !== null && (
              <p className="text-xs text-muted-foreground truncate">{file.name}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="upload-family-doc-type">
                {t('routes.family.documents.uploadDialog.type')}
              </Label>
              <Select
                id="upload-family-doc-type"
                value={type}
                onChange={(event) => {
                  setType(event.target.value as FamilyDocumentType);
                }}
                disabled={busy}
              >
                {DOCUMENT_TYPES.map((code) => (
                  <option key={code} value={code}>
                    {t(`routes.family.documents.type.${code}`)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="upload-family-doc-date">
                {t('routes.family.documents.uploadDialog.dateObserved')}
              </Label>
              <DateInput
                id="upload-family-doc-date"
                value={dateObserved}
                onChange={setDateObserved}
                disabled={busy}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="upload-family-doc-comments">
              {t('routes.family.documents.uploadDialog.comments')}
            </Label>
            <Textarea
              id="upload-family-doc-comments"
              value={comments}
              rows={3}
              onChange={(event) => {
                setComments(event.target.value);
              }}
              disabled={busy}
              placeholder={t('routes.family.documents.uploadDialog.commentsPlaceholder')}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
              {t('ui.dialog.close')}
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              loading={busy}
              loadingText={t('routes.family.documents.uploading')}
            >
              {t('routes.family.documents.uploadDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function buildSimpleDate(
  isoLike: string,
): { year: number; month?: number; day?: number } | null {
  const [yearStr, monthStr, dayStr] = isoLike.split('-');
  const year = yearStr?.length === 4 ? Number(yearStr) : NaN;
  if (!Number.isFinite(year) || year <= 0) return null;
  const month = monthStr?.length === 2 ? Number(monthStr) : NaN;
  const day = dayStr?.length === 2 ? Number(dayStr) : NaN;
  const monthValid = Number.isFinite(month) && month >= 1 && month <= 12;
  const dayValid = Number.isFinite(day) && day >= 1 && day <= 31;
  return {
    year,
    ...(monthValid ? { month } : {}),
    ...(monthValid && dayValid ? { day } : {}),
  };
}
